import {
  DataCollectionFormField,
  DataCollectionFormLogicJump,
  DataCollectionFormOptionBank,
  DataCollectionFormResponse,
  StandardQuestionTemplate,
} from 'api-clients/monolith';

export type QuestionOption = {
  value: string;
  visible?: boolean;
  label?: string;
};

export type LogicJump = DataCollectionFormLogicJump;
export type OptionBank = DataCollectionFormOptionBank;
export type EditorSettings = DataCollectionFormResponse['settings'];

/**
 * One row of the editor. `uid` is the React key and drag id: a new question has
 * no server id yet, and an id-less row still has to be stable across renders.
 */
export type EditableQuestion = Omit<
  DataCollectionFormField,
  | 'options'
  | 'restricted'
  | '_destroy'
  | 'logic_jumps_attributes'
  | 'remove_image'
> & {
  uid: string;
  options: QuestionOption[] | null;
  restricted: boolean | null;
  _destroy?: boolean;
  logic_jumps_attributes: LogicJump[];
  /** The jumps as the server sent them, so a save carries only what changed. */
  loaded_logic_jumps: LogicJump[];
  /**
   * The format as the server sent it. Only a pattern typed in the editor is
   * checked in the browser's dialect; a stored one may use syntax only Ruby
   * reads.
   */
  loaded_format?: string | null;
  /** An image picked in this session, uploaded with the save. */
  image?: File | null;
  /** Drops the stored image on save. */
  remove_image?: boolean;
};

export type QuestionErrors = {
  question?: string;
  key?: string;
  options?: string;
  format?: string;
  dateRange?: string;
  hiddenValue?: string;
  yearsOfValidation?: string;
};

export const OPTIONABLE_TYPES = ['radio', 'checkboxes', 'dropdown'];

/** Types that validate a number of years of history. */
export const HISTORY_TYPES = ['history_address', 'history_employment'] as const;

const ADDRESS_HISTORY_TYPES = [
  'address_history',
  'employment_history',
  ...HISTORY_TYPES,
] as const;

const BASE_CUSTOM_TYPES = [
  'text_field',
  'text_area',
  'radio',
  'checkboxes',
  'dropdown',
  'datepicker',
  'file',
  'address',
  'hidden_field',
] as const;

export type CustomType =
  | (typeof BASE_CUSTOM_TYPES)[number]
  | 'resume'
  | (typeof ADDRESS_HISTORY_TYPES)[number];

/** The answer types a custom question can take on this account. */
export const customTypesFor = (
  settings: Pick<EditorSettings, 'resume_enabled' | 'address_history_enabled'>,
): CustomType[] => [
  ...BASE_CUSTOM_TYPES,
  ...(settings.resume_enabled ? (['resume'] as const) : []),
  ...(settings.address_history_enabled ? ADDRESS_HISTORY_TYPES : []),
];

export const KEY_FORMAT = /^[a-z0-9_]+$/;

export const IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
export const IMAGE_ACCEPT = IMAGE_TYPES.join(',');

export const MIN_YEARS_OF_VALIDATION = 1;
export const MAX_YEARS_OF_VALIDATION = 25;

export const isOptionable = (type: string): boolean =>
  OPTIONABLE_TYPES.includes(type);

export const requiresYearsOfValidation = (type: string): boolean =>
  (HISTORY_TYPES as readonly string[]).includes(type);

/** A short or date answer the applicant can be asked to type twice. */
export const canConfirmAnswer = (question: EditableQuestion): boolean =>
  question.type === 'text_field' ||
  question.type === 'datepicker' ||
  question.key === 'ssn';

/** A custom short or date answer can be held to a format the recruiter sets. */
export const canValidateAnswer = (question: EditableQuestion): boolean =>
  !question.predefined &&
  (question.type === 'text_field' || question.type === 'datepicker');

/**
 * The regex row shows whenever a short answer carries a format: editable on a
 * custom question, read-only on a standard one whose definition sets it. A
 * validated SSN is formatted by the account's SSN validation instead.
 */
export const showsFormat = (
  question: EditableQuestion,
  settings: Pick<EditorSettings, 'data_validation_ssn_tax_id_enabled'>,
): boolean =>
  question.type === 'text_field' &&
  question.format != null &&
  !(question.key === 'ssn' && settings.data_validation_ssn_tax_id_enabled);

export const hasDateRange = (question: EditableQuestion): boolean =>
  question.min_date != null || question.max_date != null;

export const liveLogicJumps = (question: EditableQuestion): LogicJump[] =>
  question.logic_jumps_attributes.filter(jump => !jump._destroy);

/** The question triggers a jump, or another question jumps to it. */
export const hasLogicJumps = (question: EditableQuestion): boolean =>
  liveLogicJumps(question).length > 0 ||
  Boolean(question.jump_to_data_fields_exist);

const sameValues = (
  left: string[] | null | undefined,
  right: string[] | null | undefined,
) => JSON.stringify(left ?? []) === JSON.stringify(right ?? []);

/**
 * The jumps a save sends: only those the editor changed or removed. The
 * builder edits the same rows in another tab, and resending every loaded
 * jump would write its stale values back, or name an id it has deleted.
 */
export const changedLogicJumps = (question: EditableQuestion): LogicJump[] => {
  const loaded = new Map(
    question.loaded_logic_jumps.map(jump => [jump.id, jump] as const),
  );
  return question.logic_jumps_attributes.filter(jump => {
    if (jump._destroy) return true;
    const before = loaded.get(jump.id);
    return !before || !sameValues(before.values, jump.values);
  });
};

/** Every saved jump of the question, marked for removal. */
export const destroyedLogicJumps = (question: EditableQuestion): LogicJump[] =>
  question.logic_jumps_attributes
    .filter(jump => jump.id != null)
    .map(jump => ({ id: jump.id, _destroy: true }));

/** The question's live jumps triggered by one of its option values. */
export const logicJumpsOn = (
  question: EditableQuestion,
  value: string,
): LogicJump[] =>
  liveLogicJumps(question).filter(jump => (jump.values ?? []).includes(value));

/**
 * The trigger value each option holds, by option index. A jump is keyed by
 * value, and the editor renames it as the option is retyped, so the value a
 * jump currently holds for an option is tracked by the option's position
 * rather than read back from its text.
 */
export const triggerValuesByIndex = (
  question: EditableQuestion,
  options: QuestionOption[],
): Record<number, string> => {
  const triggers: Record<number, string> = {};
  options.forEach((option, index) => {
    if (option.value && logicJumpsOn(question, option.value).length > 0) {
      triggers[index] = option.value;
    }
  });
  return triggers;
};

/** Renames every value a jump triggers on, all at once, by the given map. */
const renameLogicJumpValues = (
  jumps: LogicJump[],
  renames: Map<string, string>,
): LogicJump[] =>
  jumps.map(jump =>
    jump._destroy || !(jump.values ?? []).some(value => renames.has(value))
      ? jump
      : {
          ...jump,
          values: (jump.values ?? []).map(value => renames.get(value) ?? value),
        },
  );

/**
 * Follows every trigger to its option's current value. A trigger holds its
 * last text while its option is blank or reads as another option's trigger,
 * so typing through "1" on the way to "10" never merges two jumps. Triggers
 * whose options moved onto each other's text, as a pasted swap does, move
 * together, so the jumps end on the options that now carry their text.
 * Returns the same jumps when nothing moved.
 */
export const syncLogicJumpTriggers = (
  jumps: LogicJump[],
  triggers: Record<number, string>,
  options: QuestionOption[],
): { jumps: LogicJump[]; triggers: Record<number, string> } => {
  let nextJumps = jumps;
  const nextTriggers = { ...triggers };
  for (;;) {
    const pending = new Map<number, string>();
    Object.keys(nextTriggers).forEach(key => {
      const index = Number(key);
      const value = options[index]?.value ?? '';
      if (value.trim() !== '' && value !== nextTriggers[index]) {
        pending.set(index, value);
      }
    });
    // A pending trigger is held while its text belongs to an option that
    // stays, or to one that is itself held, or to a second pending option.
    const held = new Set<number>();
    let changed = true;
    while (changed) {
      changed = false;
      pending.forEach((value, index) => {
        if (held.has(index)) return;
        const blocked = Object.entries(nextTriggers).some(([other, text]) => {
          const otherIndex = Number(other);
          if (otherIndex === index || text !== value) return false;
          return !pending.has(otherIndex) || held.has(otherIndex);
        });
        const shared = [...pending].some(
          ([other, text]) => other !== index && text === value,
        );
        if (blocked || shared) {
          held.add(index);
          changed = true;
        }
      });
    }
    const renames = new Map<string, string>();
    pending.forEach((value, index) => {
      if (!held.has(index)) renames.set(nextTriggers[index], value);
    });
    if (renames.size === 0) break;
    nextJumps = renameLogicJumpValues(nextJumps, renames);
    pending.forEach((value, index) => {
      if (!held.has(index)) nextTriggers[index] = value;
    });
  }
  return { jumps: nextJumps, triggers: nextTriggers };
};

/**
 * Drops a value from every jump that triggers on it. A jump left with no
 * value is removed: the server refuses an empty value list.
 */
export const removeLogicJumpValue = (
  jumps: LogicJump[],
  value: string,
): LogicJump[] =>
  jumps.flatMap(jump => {
    if (jump._destroy || !(jump.values ?? []).includes(value)) return [jump];
    const values = (jump.values ?? []).filter(item => item !== value);
    if (values.length > 0) return [{ ...jump, values }];
    return jump.id != null ? [{ id: jump.id, _destroy: true }] : [];
  });

const jumpLinks = (question: EditableQuestion, other: EditableQuestion) =>
  other.id != null &&
  ((question.jump_to_ids ?? []).includes(other.id) ||
    (question.logic_attached_to_ids ?? []).includes(other.id));

/**
 * Whether dragging a row across the given range passes a question it is
 * linked to by a jump. The server drops those jumps when the position lands,
 * so the move asks first.
 */
export const moveCrossesLogic = (
  rows: EditableQuestion[],
  from: number,
  to: number,
): boolean => {
  const moved = rows[from];
  const crossed =
    from < to ? rows.slice(from + 1, to + 1) : rows.slice(to, from);
  return crossed.some(other => jumpLinks(moved, other));
};

/**
 * Moves a visible row to another visible position. `from` and `to` index the
 * rows the list renders: live and not hidden. A hidden row (the BIC companion
 * of an IBAN) keeps its slot, so a drag never changes its position on the
 * server; removed rows stay at the end.
 */
export const moveShownQuestion = (
  questions: EditableQuestion[],
  from: number,
  to: number,
): EditableQuestion[] => {
  const live = questions.filter(question => !question._destroy);
  const shown = live.filter(question => !question.hidden);
  const [moved] = shown.splice(from, 1);
  shown.splice(to, 0, moved);
  let next = 0;
  return [
    ...live.map(question => {
      if (question.hidden) return question;
      const row = shown[next];
      next += 1;
      return row;
    }),
    ...questions.filter(question => question._destroy),
  ];
};

export const templateOptions = (
  template: StandardQuestionTemplate | undefined,
): QuestionOption[] =>
  (template?.options ?? []).map(option => ({
    value: String(option.value),
    label: option.label ?? undefined,
    visible: option.visible ?? true,
  }));
